/**
 * 常用選項可複選 + 可手動輸入的欄位(REQ-20260810001)。
 *
 * 原本寫死在 `CreateReceivableModal` 裡,理由是「只有那三格用,沒有第二個呼叫點」。
 * REQ-20260811003 讓出貨管理的建單 / 編輯表單也要有印刷顏色 / 稿件處理,
 * 第二個呼叫點出現了才抽出來 —— 兩邊的操作方式必須一致,複製一份會慢慢分岔。
 *
 * **沒有**沿用單位欄那套 `<input list>` + `<datalist>` —— datalist 只能單選。
 * 選項存的是**文字**(reference 的 name,不是 id),與單位欄同一個理由:
 * 單據是歷史憑證,設定頁事後改名 / 停用不該回頭改已開的單(REQ-20260805002)。
 */
import { useState } from 'react';

/** 多個項目串成一格文字時用的分隔符。`joinItems` / `splitItems` 必須對稱 */
const SEPARATOR = '、';

/**
 * 送出時的形態:用「、」把已選項目串成一個字串。
 * **一個都沒選就送 null**(不是空字串)—— 與這幾格原本
 * `setValueAs: (v) => (v === '' ? null : v)` 的行為一致,列印頁那幾格照舊留白給現場手寫。
 */
export function joinItems(items: string[]): string | null {
  return items.length > 0 ? items.join(SEPARATOR) : null;
}

/**
 * `joinItems()` 的反向:編輯既有單據時把存起來的字串還原成已選項目。
 * 舊單(或手動輸入)的值不一定對得上目前的選項清單,那就當成手動輸入的項目照樣顯示,
 * 不會因為設定頁把某個選項停用了就在編輯畫面上憑空消失。
 */
export function splitItems(value: string | null | undefined): string[] {
  if (!value) return [];
  return value
    .split(SEPARATOR)
    .map((s) => s.trim())
    .filter((s) => s !== '');
}

/**
 * reference 的 `name` 不是 unique(見 REQ-20260805002 的同一項風險),
 * 同名兩列會讓兩個 checkbox 的勾選狀態綁在一起、React key 也會撞,所以先去重。
 */
export function optionNames(rows: { name: string }[] | undefined): string[] {
  return [...new Set((rows ?? []).map((r) => r.name))];
}

/**
 * 超過這個選項數才收合起來 + 給搜尋框(REQ-20260821005)。
 *
 * 印刷加工類的常用選項容易累積到二三十筆,全部攤開就是使用者抱怨的
 * 「幾十個 checkbox 密密麻麻」;但只有兩三個選項時收合反而多一次點擊,
 * 所以短清單維持原本一打開就看得到的樣子。
 */
const COLLAPSE_THRESHOLD = 6;

export function MultiSelectField(props: {
  label: string;
  testId: string;
  options: string[];
  selected: string[];
  onChange: (next: string[]) => void;
  /**
   * 只能挑一個值(REQ-20260821007)。**預設 true**,既有的呼叫端一個字都不用改。
   *
   * 進貨單的「加工項目」「加工 / 後製加工」是單選:一張單本來就能用「新增品項」
   * 加多列分別記錄不同加工,同一列裡勾多個會讓金額歸屬變得沒有意義。
   * 出貨管理那幾格仍然是複選(REQ-20260819008 的決定沒有被推翻)。
   * 單選還多一件事:**勾好之後自動收合**(REQ-20260821009,見 `toggle()`)。
   */
  multiple?: boolean;
  /**
   * 精簡版面(REQ-20260905002):標題列右邊放一顆「已選 M/N」切換鈕,值列是一個
   * 輸入框外觀的方框(chip + 手動輸入格),收合狀態下**整格一行高**,
   * 與同一列的單值 `Field` 對得齊。**預設 false**,既有呼叫端一個字都不用改。
   *
   * 進貨單商品採購版的「單位」用它:正式環境的貨品單位有二十幾筆,一般版面即使
   * 收合仍是 legend + 收合鈕 + chip + 輸入框四行,夾在「數量」「單價」中間高一大截。
   *
   * 只是**多一個 opt-in 的版面**,不是改共用元件的預設外觀 ——
   * 元件的 `text-sm text-slate-700` 與 `CreateReceivableModal` 周邊欄位一致,
   * 單方面改掉會把那張表單弄得參差(REQ-20260903002 §2.1 已查證)。
   */
  compact?: boolean;
  /**
   * 一掛上去就直接展開選項清單(REQ-20260905006)。**預設 false**,不傳時
   * DOM 與 class 逐字不變 —— 這只是多一個 opt-in 的初始狀態,不是改預設外觀。
   *
   * 進貨單的「數量」「厚度」把單位挑選框收在一顆單位鈕後面,鈕按下去才把這顆元件
   * 掛出來;若還要再按一次元件自己的切換鈕才看得到選項,選一個單位就得點兩下。
   * 只有初始值 —— 之後仍由元件自己的切換鈕(與單選挑完的自動收合)控制。
   */
  defaultExpanded?: boolean;
  /**
   * 把最底下那個手動輸入框的 DOM 節點交出去(REQ-20260911001)。**預設不傳**,
   * 不傳時 DOM / class / 行為逐字不變 —— 這顆元件出貨單 / 應收單 / 設定頁都在用。
   *
   * 進貨單商品採購版的「單位」夾在數量與單價中間,要讓鍵盤從數量跳進來、
   * 再往單價跳出去,呼叫端就得拿得到這一格才 focus 得了它。
   */
  inputRef?: React.Ref<HTMLInputElement> | undefined;
  /**
   * 手動輸入框的 keydown 再轉給呼叫端(REQ-20260911001)。**預設不傳**。
   *
   * ⚠️ 在元件自己那段「Enter → `commitDraft()`」之**後**才呼叫 ——
   * 順序是「先把打的字收進去,再跳格」。反過來的話,打完單位直接按 Enter
   * 會先把焦點搬走,打的字要等 `onBlur` 才收,呼叫端拿到的是還沒收字的狀態。
   */
  onInputKeyDown?: ((e: React.KeyboardEvent<HTMLInputElement>) => void) | undefined;
}): JSX.Element {
  const {
    label,
    testId,
    options,
    selected,
    onChange,
    multiple = true,
    compact = false,
    defaultExpanded = false,
    inputRef,
    onInputKeyDown,
  } = props;
  const [draft, setDraft] = useState('');
  const [expanded, setExpanded] = useState(defaultExpanded);
  /** 選項清單的關鍵字過濾。**純前端、不送出**,與下面「其他」那格是兩回事 */
  const [query, setQuery] = useState('');

  const collapsible = options.length > COLLAPSE_THRESHOLD;
  // 精簡版一律預設收合、**不看 `COLLAPSE_THRESHOLD`**:選項只有兩三個也不能攤開來,
  // 「一行高」就是這個模式存在的理由
  const showOptions = options.length > 0 && (compact ? expanded : !collapsible || expanded);
  const keyword = query.trim().toLowerCase();
  const visibleOptions =
    keyword === '' ? options : options.filter((name) => name.toLowerCase().includes(keyword));

  /** 單選時「挑一個」= 取代原本的選取;再點同一個仍然是取消(chip 的移除也走這裡) */
  const toggle = (name: string): void => {
    if (selected.includes(name)) {
      onChange(selected.filter((s) => s !== name));
      return;
    }
    onChange(multiple ? [...selected, name] : [name]);
    // 單選模式選定即完成,自動收合減少畫面雜訊(REQ-20260821009)。
    // 複選要連續勾好幾項,收起來反而每勾一個就得重新展開
    if (!multiple) setExpanded(false);
  };

  /**
   * 手動輸入的文字進袋:逗號(半形 / 全形)可一次加多個,空白與已選過的略過。
   * 單選時整串當成**一個**值並取代原本的選取 —— 不拆逗號、不附加。
   */
  const commitDraft = (): void => {
    if (!multiple) {
      const value = draft.trim();
      if (value !== '') onChange([value]);
      setDraft('');
      return;
    }
    const added = draft
      .split(/[,，]/)
      .map((s) => s.trim())
      .filter((s) => s !== '' && !selected.includes(s));
    if (added.length > 0) onChange([...selected, ...added]);
    setDraft('');
  };

  const legendText = multiple ? `${label}(可複選)` : label;

  /** 展開後的選項面板。兩種版面內容完全相同,只有擺放的位置不同 */
  const optionsPanel = showOptions ? (
    <>
      {/* 搜尋框只在清單長到需要收合時出現 —— 六個以內用眼睛找比打字快。
          與下面「其他」那格刻意分開:這裡打字只過濾,不會把字加進已選項目 */}
      {collapsible ? (
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="w-full border rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          placeholder="搜尋選項(只過濾清單,不會加入)"
          data-testid={`${testId}-search`}
        />
      ) : null}

      {visibleOptions.length > 0 ? (
        <div className="flex flex-wrap gap-x-3 gap-y-1">
          {visibleOptions.map((name) => (
            <label key={name} className="flex items-center gap-1 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={selected.includes(name)}
                onChange={() => toggle(name)}
                data-testid={`${testId}-option`}
              />
              {name}
            </label>
          ))}
        </div>
      ) : (
        <p className="text-xs text-slate-400">
          沒有符合「{query.trim()}」的選項 —— 清單外的值請用下面的輸入框加入
        </p>
      )}
    </>
  ) : null;

  /** 已選項目(含手動輸入的);點一下移除,順序 = 選取順序 = 串接順序 */
  const chipList = selected.map((name) => (
    <button
      key={name}
      type="button"
      onClick={() => toggle(name)}
      title="點一下移除"
      className="flex items-center gap-1 rounded-full border border-blue-300 bg-blue-50 px-2 py-0.5 text-xs text-blue-800 hover:bg-blue-100"
    >
      {name}
      <span aria-hidden>×</span>
    </button>
  ));

  const draftInput = (
    <input
      type="text"
      value={draft}
      ref={inputRef}
      onChange={(e) => setDraft(e.target.value)}
      onKeyDown={(e) => {
        // Enter 在 form 裡預設會送出整張表單,一定要擋掉
        if (e.key === 'Enter' || e.key === ',' || e.key === '，') {
          e.preventDefault();
          commitDraft();
        }
        // 收完字才把事件轉給呼叫端(REQ-20260911001)—— 見 `onInputKeyDown` 的說明。
        // 上面已經 `preventDefault()` 過的鍵再被呼叫端擋一次是 no-op,不必特別分流
        onInputKeyDown?.(e);
      }}
      // 打完沒按 Enter 就直接去點「建立」時 blur 會先發生 —— 不讓打好的字靜默消失
      onBlur={commitDraft}
      // 與上面的搜尋框、以及呼叫端表單其餘欄位同一個字級(REQ-20260821007)——
      // 原本沒寫 `text-sm`,瀏覽器預設 16px 讓這一格比同一張表單的其他輸入框高一截
      // (38px vs 30px),整個區塊看起來就胖了一圈。
      // 精簡版的外框改掛在值列那個方框上,這裡只留字級與 flex 伸縮 ——
      // ⚠️ `min-w-0` 不能拿掉:flex 子項的 input 有瀏覽器預設最小寬(實測約 172px),
      // 少了它會把值列撐爆,md 斷點下直接破版
      className={
        compact
          ? 'flex-1 min-w-0 border-0 bg-transparent p-0 text-sm focus:outline-none'
          : 'w-full border rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400'
      }
      placeholder={
        compact
          ? '輸入或選擇'
          : multiple
            ? '其他(打字後按 Enter 或逗號加入)'
            : '其他(打字後按 Enter 取代選取)'
      }
      data-testid={`${testId}-input`}
    />
  );

  // 精簡版(REQ-20260905002):標題列 + 一格輸入框外觀的值列,收合狀態下就是一行高
  if (compact) {
    return (
      <fieldset className="grid gap-1" data-testid={testId}>
        {/* `<legend>` 是 fieldset 的直屬子節點時排不進 flex(瀏覽器把它當成框線缺口上
            的標題另外排),要與切換鈕併成一行就得包一層 div。
            **不要**改用 `<label>` 包起來繞過 —— `<button>` 是 labelable element,
            包進 label 會讓 `getByLabelText` 一次抓到兩個元素 */}
        <div className="flex items-center justify-between gap-2">
          <legend className="text-xs text-slate-600">{legendText}</legend>
          {/* 精簡版一律渲染這顆鈕(不看 `COLLAPSE_THRESHOLD`),文字用「已選 M/N」——
              收起來之後總得看得出這一格有沒有值、清單有多長 */}
          <button
            type="button"
            onClick={() => setExpanded((prev) => !prev)}
            aria-expanded={expanded}
            className="shrink-0 text-xs text-blue-600 hover:underline"
            data-testid={`${testId}-toggle`}
          >
            {`已選 ${selected.length}/${options.length} ${expanded ? '▴' : '▾'}`}
          </button>
        </div>

        {/* 值列:左邊是已選的 chip,右邊接著打得了字的輸入格,整格看起來就是一個
            輸入框(focus 的環掛在方框上,裡面那個 input 自己沒有框)。
            `-selected` 掛在這個方框上、chip 直接排在它底下沒有再包一層 ——
            既有「取 `-selected` 底下的 button」那套 chip 取法照樣成立 */}
        <div
          className="flex flex-wrap items-center gap-1 border rounded-md px-2 py-1 focus-within:ring-2 focus-within:ring-blue-400"
          data-testid={`${testId}-selected`}
        >
          {chipList}
          {draftInput}
        </div>

        {/* 展開的清單接在值列**下面**,維持 in-flow:呼叫端那一列有 `items-start`,
            變高不會把鄰格拉長,不必為此做浮層(還得處理外點關閉與 z-index) */}
        {optionsPanel}
      </fieldset>
    );
  }

  return (
    <fieldset className="grid gap-1" data-testid={testId}>
      <legend className="text-sm text-slate-700">{legendText}</legend>

      {/* 收合狀態下唯一看得到的選項入口 —— 已選幾項要寫出來,
          不然收起來之後看起來像什麼都沒選(chip 那排在下面照樣顯示) */}
      {collapsible ? (
        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          aria-expanded={expanded}
          className="justify-self-start text-sm text-blue-600 hover:underline"
          data-testid={`${testId}-toggle`}
        >
          {expanded
            ? '收合選項'
            : `展開選項(共 ${options.length} 項${selected.length > 0 ? `,已選 ${selected.length} 項` : ''})`}
        </button>
      ) : null}

      {optionsPanel}

      {selected.length > 0 ? (
        <div className="flex flex-wrap gap-1" data-testid={`${testId}-selected`}>
          {chipList}
        </div>
      ) : null}

      {draftInput}
    </fieldset>
  );
}
